import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Admin.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function formatUSDC(raw) {
  if (!raw) return '$0.00';
  return `$${(Number(raw) / 1_000_000).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function authHeaders() {
  const token = sessionStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Lock screen ────────────────────────────────────────────────────────────
function LockScreen({ onUnlock }) {
  const [pw, setPw]       = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      sessionStorage.setItem('admin_token', data.token);
      onUnlock();
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      setPw('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-lock">
      <div className="admin-lock__card">
        <div className="admin-lock__title">Admin <span>Panel</span></div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            className="admin-lock__input"
            type="password"
            placeholder="Enter password"
            value={pw}
            onChange={e => { setPw(e.target.value); setError(''); }}
            autoFocus
          />
          {error && <div className="admin-lock__error">{error}</div>}
          <button className="admin-btn" type="submit" disabled={loading}>
            {loading ? 'Verifying…' : 'Unlock'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Create tournament form ─────────────────────────────────────────────────
function CreateForm({ onCreated }) {
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm]       = useState({
    entryFee:     '1',
    game:         'apex',
    maxPlayers:   '50',
    windowMinutes: '30',
    usdcAddress:  '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
  });

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function handleGameChange(e) {
    const g = e.target.value;
    const mins = g === 'cs2' ? '60' : g === 'tarkov' ? '25' : '30';
    setForm(f => ({ ...f, game: g, windowMinutes: mins }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    const entryFeeUsdc = Math.round(parseFloat(form.entryFee) * 1_000_000);
    if (!entryFeeUsdc || entryFeeUsdc <= 0) return setError('Invalid entry fee.');

    setLoading(true);
    try {
      const payload = {
        entryFee:      String(entryFeeUsdc),
        usdcAddress:   form.usdcAddress,
        game:          form.game,
        maxPlayers:    Number(form.maxPlayers) || 50,
        windowMinutes: Number(form.windowMinutes) || 30,
      };
      // CS2 / Fortnite / Tarkov: 5-min resolve delay so match stats flush before resolving
      if (form.game === 'cs2' || form.game === 'fortnite' || form.game === 'tarkov') payload.resolveDelay = 300;

      const { data } = await axios.post(
        `${API}/tournament/create`,
        payload,
        { headers: authHeaders() }
      );
      setSuccess(`Tournament #${data.tournament.id} created!`);
      onCreated();
      setTimeout(() => { setOpen(false); setSuccess(''); }, 2000);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired — please log in again.');
        sessionStorage.removeItem('admin_token');
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setLoading(false);
    }
  }

  if (!open) {
    return (
      <button className="admin-btn admin-btn--sm" onClick={() => setOpen(true)}>
        + Create Tournament
      </button>
    );
  }

  function applyTestMode() {
    const mins = form.game === 'cs2' ? '60' : form.game === 'tarkov' ? '25' : '30';
    setForm(f => ({ ...f, maxPlayers: '2', entryFee: '0.01', windowMinutes: mins }));
  }

  return (
    <div className="admin__form-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>New Tournament</div>
        <button
          type="button"
          onClick={applyTestMode}
          style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 6, border: '1px solid #FFB80066', background: '#FFB80015', color: '#FFB800', cursor: 'pointer', letterSpacing: '0.5px' }}
          title="Sets max players to 2 so you can test the full flow with just 2 wallets"
        >
          🧪 Test Mode (2 players)
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="admin__form-row">
          <div className="admin__field">
            <label>Game</label>
            <select style={{ width: '100%', background: '#ffffff08', border: '1px solid #00D4FF33', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: 'var(--text-primary)', outline: 'none' }}
              value={form.game} onChange={handleGameChange}>
              <option value="apex">Apex Legends</option>
              <option value="fortnite">Fortnite</option>
              <option value="cs2">CS2 Premier</option>
              <option value="tarkov">Tarkov Arena</option>
            </select>
          </div>
          <div className="admin__field">
            <label>Entry Fee (USDC)</label>
            <input type="number" min="0.01" step="0.01" placeholder="1.00"
              value={form.entryFee} onChange={e => set('entryFee', e.target.value)} />
          </div>
          <div className="admin__field">
            <label>Max Players</label>
            <input type="number" min="2" max="1000" step="1" placeholder="50"
              value={form.maxPlayers} onChange={e => set('maxPlayers', e.target.value)}
              style={form.maxPlayers === '2' ? { borderColor: '#FFB800aa' } : {}} />
            <div style={{ fontSize: 11, color: form.maxPlayers === '2' ? '#FFB800' : 'var(--text-secondary)', marginTop: 4 }}>
              {form.maxPlayers === '2' ? '🧪 Test mode — lobby fills after 2 players' : `Window auto-starts when full (${form.windowMinutes}-min)`}
            </div>
          </div>
          <div className="admin__field">
            <label>Window (minutes)</label>
            <input type="number" min="5" max="240" step="5" placeholder="30"
              value={form.windowMinutes} onChange={e => set('windowMinutes', e.target.value)} />
            {form.game === 'cs2' && (
              <div style={{ fontSize: 11, color: '#FFB800', marginTop: 4 }}>
                CS2: +5 min resolve delay after window (Premier match can finish)
              </div>
            )}
            {form.game === 'fortnite' && (
              <div style={{ fontSize: 11, color: '#FFDE00', marginTop: 4 }}>
                Fortnite: +5 min resolve delay (Epic stats API needs time to flush)
              </div>
            )}
            {form.game === 'tarkov' && (
              <div style={{ fontSize: 11, color: '#C0A060', marginTop: 4 }}>
                Tarkov: +5 min resolve delay · Last Hero mode · K/D · XP tiebreaker
              </div>
            )}
          </div>
        </div>
        <div className="admin__field">
          <label>USDC Contract Address</label>
          <input type="text" placeholder="0x..."
            value={form.usdcAddress} onChange={e => set('usdcAddress', e.target.value)} />
        </div>
        {error   && <div className="admin__form-error">{error}</div>}
        {success && <div className="admin__form-success">{success}</div>}
        <div className="admin__form-actions">
          <button type="button" className="admin-btn admin-btn--sm admin-btn--ghost"
            onClick={() => { setOpen(false); setError(''); }}>
            Cancel
          </button>
          <button type="submit" className="admin-btn admin-btn--sm" disabled={loading}>
            {loading ? 'Deploying…' : 'Deploy & Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Manual winner picker ───────────────────────────────────────────────────
function ManualResolvePanel({ tournament, onResolved, onCancel }) {
  const [players, setPlayers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [confirming, setConfirming] = useState(null); // wallet address being confirmed
  const [resolving, setResolving]   = useState(false);
  const [removing, setRemoving]     = useState(null); // wallet being removed
  const [error, setError]           = useState('');

  function loadPlayers() {
    axios.get(`${API}/tournament/${tournament.id}/players`, { headers: authHeaders() })
      .then(r => setPlayers(r.data.players))
      .catch(e => setError(e.response?.data?.error || 'Could not load players'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadPlayers(); }, [tournament.id]); // eslint-disable-line

  async function handlePick(wallet, displayName) {
    if (confirming !== wallet) { setConfirming(wallet); return; }
    setResolving(true); setError('');
    try {
      await axios.post(
        `${API}/tournament/${tournament.id}/resolve`,
        { manualWinnerWallet: wallet },
        { headers: authHeaders() }
      );
      onResolved(`Paid out to ${displayName}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resolve');
      setConfirming(null);
    } finally { setResolving(false); }
  }

  async function handleRemoveEntry(wallet, displayName) {
    if (!window.confirm(`Remove entry for ${displayName} (${wallet.slice(0,6)}…${wallet.slice(-4)})?\n\nThis lets them re-join. Only use this for ghost/stuck entries.`)) return;
    setRemoving(wallet);
    setError('');
    try {
      await axios.delete(
        `${API}/tournament/${tournament.id}/entry`,
        { data: { wallet }, headers: authHeaders() }
      );
      loadPlayers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove entry');
    } finally {
      setRemoving(null);
    }
  }

  return (
    <div className="admin__manual-panel">
      <div className="admin__manual-title">
        Select Winner — Tournament #{tournament.id}
        <span className="admin__manual-game">{tournament.game === 'fortnite' ? '⚡ Fortnite' : tournament.game === 'cs2' ? '🔫 CS2' : tournament.game === 'tarkov' ? '☠️ Tarkov' : '🎮 Apex'}</span>
      </div>
      <div className="admin__manual-sub">
        Verify kills manually (screenshot / in-game), then click the winner below.
        90% of {formatUSDC(tournament.prize_pool)} will be sent to their wallet on-chain.
      </div>

      {loading && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading players…</div>}
      {error   && <div className="admin__form-error">{error}</div>}

      {!loading && players.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No players found.</div>
      )}

      {players.map(p => {
        const displayName = p.tarkov_player_id || p.steam_player_id || p.epic_player_id || p.apex_player_id || '—';
        const isConfirming = confirming === p.wallet_address;
        const isRemoving   = removing === p.wallet_address;
        return (
          <div key={p.wallet_address} className="admin__player-row">
            <div className="admin__player-info">
              <div className="admin__player-name">{displayName}</div>
              <div className="admin__player-wallet">{p.wallet_address.slice(0,6)}…{p.wallet_address.slice(-4)}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className={`admin-btn admin-btn--sm${isConfirming ? ' admin__confirm-btn' : ''}`}
                onClick={() => handlePick(p.wallet_address, displayName)}
                disabled={resolving || isRemoving}
              >
                {resolving && isConfirming ? 'Paying…' : isConfirming ? '✓ Confirm payout' : 'Set winner'}
              </button>
              <button
                className="admin-btn admin-btn--sm"
                style={{ background: 'transparent', border: '1px solid #ff006e55', color: '#ff006e', fontSize: 11 }}
                onClick={() => handleRemoveEntry(p.wallet_address, displayName)}
                disabled={resolving || isRemoving}
                title="Remove ghost/stuck entry so player can re-join"
              >
                {isRemoving ? '…' : '✕ Remove'}
              </button>
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={onCancel} disabled={resolving}>
          Cancel
        </button>
        {confirming && (
          <div style={{ fontSize: 11, color: '#FFB800', alignSelf: 'center' }}>
            Click "Confirm payout" again to send funds on-chain.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main admin panel ───────────────────────────────────────────────────────
export default function Admin() {
  const [authed, setAuthed]           = useState(!!sessionStorage.getItem('admin_token'));
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [resolving, setResolving]     = useState({});
  const [resolveMsg, setResolveMsg]   = useState({});
  const [pickerOpen, setPickerOpen]   = useState({}); // tournamentId → bool
  const [aiLoading, setAiLoading]         = useState({}); // tournamentId → bool
  const [aiResult, setAiResult]           = useState({}); // tournamentId → analysis
  const [aiConfirming, setAiConfirming]   = useState({}); // tournamentId → bool
  const [cancelling, setCancelling]       = useState({}); // tournamentId → bool
  const [cancelMsg, setCancelMsg]         = useState({}); // tournamentId → string

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tournament/history?limit=100`);
      setTournaments(data.tournaments);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (authed) fetchAll(); }, [authed, fetchAll]);

  async function handleAutoResolve(id) {
    setResolving(r => ({ ...r, [id]: true }));
    setResolveMsg(m => ({ ...m, [id]: '' }));
    try {
      await axios.post(`${API}/tournament/${id}/resolve`, {}, { headers: authHeaders() });
      setResolveMsg(m => ({ ...m, [id]: 'Resolved!' }));
      fetchAll();
    } catch (err) {
      if (err.response?.status === 401) {
        setResolveMsg(m => ({ ...m, [id]: 'Session expired — please log in again' }));
        sessionStorage.removeItem('admin_token');
        setAuthed(false);
      } else {
        setResolveMsg(m => ({ ...m, [id]: err.response?.data?.error || 'Failed' }));
      }
    } finally {
      setResolving(r => ({ ...r, [id]: false }));
    }
  }

  async function handleAiAnalyze(id) {
    setAiLoading(a => ({ ...a, [id]: true }));
    setAiResult(a => ({ ...a, [id]: null }));
    setAiConfirming(a => ({ ...a, [id]: false }));
    try {
      const { data } = await axios.post(
        `${API}/admin/ai-analyze/${id}`,
        {},
        { headers: authHeaders() }
      );
      setAiResult(a => ({ ...a, [id]: data.analysis }));
    } catch (err) {
      setAiResult(a => ({ ...a, [id]: { error: err.response?.data?.error || 'AI analysis failed' } }));
    } finally {
      setAiLoading(a => ({ ...a, [id]: false }));
    }
  }

  async function handleAiConfirm(tournamentId, winnerWallet, winnerName) {
    setAiConfirming(a => ({ ...a, [tournamentId]: true }));
    try {
      await axios.post(
        `${API}/tournament/${tournamentId}/resolve`,
        { manualWinnerWallet: winnerWallet },
        { headers: authHeaders() }
      );
      setResolveMsg(m => ({ ...m, [tournamentId]: `✓ Paid out to ${winnerName}` }));
      setAiResult(a => ({ ...a, [tournamentId]: null }));
      fetchAll();
    } catch (err) {
      setResolveMsg(m => ({ ...m, [tournamentId]: err.response?.data?.error || 'Failed to resolve' }));
    } finally {
      setAiConfirming(a => ({ ...a, [tournamentId]: false }));
    }
  }



  async function handleCancel(tournament) {
    setCancelling(c => ({ ...c, [tournament.id]: true }));
    setCancelMsg(m => ({ ...m, [tournament.id]: '' }));
    try {
      // Backend calls cancelTournament() on-chain using the server's private key
      // — no wallet required from the admin
      const { data } = await axios.patch(
        `${API}/tournament/${tournament.id}/cancel`,
        {},
        { headers: authHeaders() }
      );
      setCancelMsg(m => ({ ...m, [tournament.id]: data.message || 'Cancelled — players refunded on-chain' }));
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Cancel failed';
      setCancelMsg(m => ({ ...m, [tournament.id]: msg }));
    } finally {
      setCancelling(c => ({ ...c, [tournament.id]: false }));
    }
  }

  function logout() {
    sessionStorage.removeItem('admin_token');
    setAuthed(false);
  }

  if (!authed) return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Admin | EZGAME</title>
      </Helmet>
      <LockScreen onUnlock={() => setAuthed(true)} />
    </>
  );

  const totalRake = tournaments
    .filter(t => t.status === 'resolved')
    .reduce((sum, t) => sum + Math.floor(Number(t.prize_pool) * 0.1), 0);

  const resolved    = tournaments.filter(t => t.status === 'resolved').length;
  const filling     = tournaments.filter(t => t.status === 'filling').length;
  const active      = tournaments.filter(t => t.status === 'active').length;
  const needsReview = tournaments.filter(t => t.status === 'needs_review').length;

  return (
    <div className="admin">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Admin Panel | EZGAME</title>
      </Helmet>
      <div className="admin__header">
        <h1 className="admin__title">Admin <span>Panel</span></h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Link
            to="/admin/dashboard"
            className="admin-btn admin-btn--sm"
            style={{ textDecoration: 'none' }}
          >
            🚀 Launch Dashboard
          </Link>
          <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={logout}>
            Logout
          </button>
        </div>
      </div>

      <div className="admin__earnings">
        <div>
          <div className="admin__earnings-label">Platform Earnings (10% rake)</div>
          <div className="admin__earnings-val">{formatUSDC(totalRake)}</div>
        </div>
        <div className="admin__earnings-meta">
          <div className="admin__stat">
            <div className="admin__stat-val">{tournaments.length}</div>
            <div className="admin__stat-lbl">Total</div>
          </div>
          <div className="admin__stat">
            <div className="admin__stat-val">{filling}</div>
            <div className="admin__stat-lbl">Filling</div>
          </div>
          <div className="admin__stat">
            <div className="admin__stat-val">{active}</div>
            <div className="admin__stat-lbl">Active</div>
          </div>
          <div className="admin__stat">
            <div className="admin__stat-val">{resolved}</div>
            <div className="admin__stat-lbl">Resolved</div>
          </div>
          {needsReview > 0 && (
            <div className="admin__stat" style={{ border: '1px solid #FF006E66', borderRadius: 8, padding: '4px 10px', background: '#FF006E0d' }}>
              <div className="admin__stat-val" style={{ color: '#FF006E' }}>{needsReview}</div>
              <div className="admin__stat-lbl" style={{ color: '#FF006E' }}>⚠ Review</div>
            </div>
          )}
        </div>
      </div>

      <div className="admin__section">
        <div className="admin__section-header">
          <span className="admin__section-title">Create Tournament</span>
        </div>
        <CreateForm onCreated={fetchAll} />
      </div>

      <div className="admin__section">
        <div className="admin__section-header">
          <span className="admin__section-title">All Tournaments</span>
          <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={fetchAll}>
            Refresh
          </button>
        </div>

        {loading && <div className="admin__empty">Loading…</div>}
        {!loading && tournaments.length === 0 && (
          <div className="admin__empty">No tournaments yet.</div>
        )}
        {/* Manual winner pickers — shown below table when open */}
        {tournaments.filter(t => pickerOpen[t.id]).map(t => (
          <ManualResolvePanel
            key={t.id}
            tournament={t}
            onResolved={(msg) => {
              setResolveMsg(m => ({ ...m, [t.id]: msg }));
              setPickerOpen(p => ({ ...p, [t.id]: false }));
              fetchAll();
            }}
            onCancel={() => setPickerOpen(p => ({ ...p, [t.id]: false }))}
          />
        ))}

        {!loading && tournaments.length > 0 && (
          <div className="admin__table-wrap">
            <table className="admin__table">
              <thead>
                <tr>
                  <th>#</th><th>Game</th><th>Status</th><th>Entry Fee</th>
                  <th>Prize Pool</th><th>Players</th><th>Window End</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tournaments.map(t => (
                  <tr key={t.id}>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>#{t.id}</td>
                    <td>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                        background: t.game === 'fortnite' ? '#FFDE0015' : t.game === 'cs2' ? '#FF6B0015' : t.game === 'tarkov' ? '#C0A06015' : '#00D4FF15',
                        color: t.game === 'fortnite' ? '#FFDE00' : t.game === 'cs2' ? '#FF6B00' : t.game === 'tarkov' ? '#C0A060' : '#00D4FF',
                        border: `1px solid ${t.game === 'fortnite' ? '#FFDE0033' : t.game === 'cs2' ? '#FF6B0033' : t.game === 'tarkov' ? '#C0A06033' : '#00D4FF33'}` }}>
                        {t.game === 'fortnite' ? 'FN' : t.game === 'cs2' ? 'CS2' : t.game === 'tarkov' ? 'TARKOV' : 'APEX'}
                      </span>
                    </td>
                    <td>
                      <span className={`admin__badge admin__badge--${t.status}`}>{t.status}</span>
                    </td>
                    <td>{formatUSDC(t.entry_fee)}</td>
                    <td>
                      <span className="admin__prize">{formatUSDC(Math.floor(Number(t.prize_pool) * 0.9))}</span>
                      <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>
                        pool: {formatUSDC(t.prize_pool)}
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-primary)' }}>{t.player_count} / {t.max_players || 100}</td>
                    <td>{formatDate(t.window_end)}</td>
                    <td>
                      {(t.status === 'active' || t.status === 'filling' || t.status === 'needs_review') ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
                          {t.status === 'needs_review' && (
                            <div style={{ fontSize: 10, color: '#FF006E', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                              ⚠ Auto-resolve failed — manual review required
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {(t.status === 'active' || t.status === 'needs_review') && !pickerOpen[t.id] && (
                              <button
                                className="admin-btn admin-btn--sm"
                                onClick={() => setPickerOpen(p => ({ ...p, [t.id]: true }))}
                                disabled={resolving[t.id]}
                              >
                                {resolving[t.id] ? 'Resolving…' : 'Resolve'}
                              </button>
                            )}
                            {(t.status === 'active' || t.status === 'needs_review') && (t.game === 'fortnite' || t.game === 'cs2' || t.game === 'tarkov') && !pickerOpen[t.id] && (
                              <button
                                className="admin-btn admin-btn--sm admin-btn--ghost"
                                onClick={() => handleAiAnalyze(t.id)}
                                disabled={aiLoading[t.id]}
                                title="Ask Claude AI to analyze stats and recommend winner"
                              >
                                {aiLoading[t.id] ? 'Analyzing…' : '🤖 AI Analyze'}
                              </button>
                            )}
                            {(t.status === 'active' || t.status === 'filling' || t.status === 'needs_review') && t.contract_address && (
                              <button
                                className="admin-btn admin-btn--sm"
                                style={{ background: 'linear-gradient(90deg,#FF006E,#ff5090)', animation: 'none' }}
                                onClick={() => handleCancel(t)}
                                disabled={cancelling[t.id]}
                                title="Cancel tournament and refund all players on-chain"
                              >
                                {cancelling[t.id] ? 'Cancelling…' : 'Cancel'}
                              </button>
                            )}
                          </div>
                          {resolveMsg[t.id] && (
                            <div className="admin__resolve-msg">{resolveMsg[t.id]}</div>
                          )}
                          {t.last_error && !resolveMsg[t.id] && (
                            <div style={{ fontSize: 10, color: '#FF006E', marginTop: 4, fontFamily: 'monospace', wordBreak: 'break-word' }}>
                              ⚠ Last error: {t.last_error}
                            </div>
                          )}
                          {cancelMsg[t.id] && (
                            <div className="admin__resolve-msg" style={{ color: cancelling[t.id] ? undefined : cancelMsg[t.id].startsWith('Cancel') ? '#00D4FF' : '#FF006E' }}>
                              {cancelMsg[t.id]}
                            </div>
                          )}
                          {/* AI Analysis result panel */}
                          {aiResult[t.id] && !aiResult[t.id].error && (
                            <div style={{ marginTop: 4, background: '#8B00FF0d', border: '1px solid #8B00FF33', borderRadius: 8, padding: '10px 12px', fontSize: 11 }}>
                              <div style={{ color: '#C084FC', fontWeight: 700, marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
                                🤖 Claude AI Recommendation
                                <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4,
                                  background: aiResult[t.id].confidence === 'high' ? '#00D4FF18' : aiResult[t.id].confidence === 'medium' ? '#FFB80018' : '#FF006E18',
                                  color: aiResult[t.id].confidence === 'high' ? '#00D4FF' : aiResult[t.id].confidence === 'medium' ? '#FFB800' : '#FF006E',
                                  border: `1px solid ${aiResult[t.id].confidence === 'high' ? '#00D4FF33' : aiResult[t.id].confidence === 'medium' ? '#FFB80033' : '#FF006E33'}`,
                                  textTransform: 'uppercase' }}>
                                  {aiResult[t.id].confidence} confidence
                                </span>
                              </div>
                              {aiResult[t.id].flagged && (
                                <div style={{ background: '#FF006E15', border: '1px solid #FF006E33', borderRadius: 6, padding: '5px 8px', marginBottom: 6, color: '#FF006E', fontSize: 10 }}>
                                  ⚠️ {aiResult[t.id].flagReason}
                                </div>
                              )}
                              <div style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 4 }}>
                                Winner: {aiResult[t.id].winnerId}
                                <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: 6 }}>
                                  +{aiResult[t.id].killsDelta}k / +{aiResult[t.id].xpDelta}xp
                                </span>
                              </div>
                              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>
                                {aiResult[t.id].reasoning}
                              </div>
                              {/* All players breakdown */}
                              {aiResult[t.id].allPlayers?.length > 0 && (
                                <div style={{ borderTop: '1px solid #ffffff0a', paddingTop: 6, marginBottom: 8 }}>
                                  {aiResult[t.id].allPlayers.map((p, i) => (
                                    <div key={p.epicPlayerId} style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0',
                                      color: i === 0 ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                                      <span>{i === 0 ? '🥇' : i === 1 ? '🥈' : '  '} {p.epicPlayerId}</span>
                                      <span style={{ fontFamily: 'monospace', color: i === 0 ? '#C084FC' : undefined }}>
                                        +{p.killsDelta}k / +{p.xpDelta}xp
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {!aiConfirming[t.id] ? (
                                <button
                                  className="admin-btn admin-btn--sm"
                                  style={{ background: 'linear-gradient(90deg,#8B00FF,#C084FC)', animation: 'none', width: '100%' }}
                                  onClick={() => handleAiConfirm(t.id, aiResult[t.id].winnerWallet, aiResult[t.id].winnerId)}
                                >
                                  ✓ Confirm — Pay {aiResult[t.id].winnerId}
                                </button>
                              ) : (
                                <button className="admin-btn admin-btn--sm" disabled style={{ width: '100%' }}>
                                  Sending payout…
                                </button>
                              )}
                            </div>
                          )}
                          {aiResult[t.id]?.error && (
                            <div style={{ fontSize: 11, color: '#FF006E', marginTop: 4 }}>
                              {aiResult[t.id].error}
                            </div>
                          )}
                        </div>
                      ) : t.status === 'resolved' ? (
                        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                          {t.winner_kills != null ? (
                            <>
                              <div style={{ color: '#00D4FF', fontWeight: 700 }}>⚔️ {t.winner_kills} kills</div>
                              {t.winner_xp != null && (
                                <div style={{ color: 'var(--text-secondary)' }}>+{Number(t.winner_xp).toLocaleString()} xp</div>
                              )}
                            </>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)' }}>—</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
